<template>
  <div class="mb-7 md:space-y-24">
    <slot
      :current-step
      name="sticky-bar"
      :selected-options
      :total-steps="funnel.length"
    />

    <StepIncompatibleKeyboard
      v-if="!hasKeyboardStep"
      :breadcrumb
      class="pb-[144px] pt-0 md:pb-[44px] md:pt-24"
      :price
      :product
      :technical-specifications
    />

    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
    <aside
      v-for="(step, index) in funnel"
      :key="step.id"
      ref="steps"
      class="py-32 md:py-36"
      @focus="() => handleFocusedStep(index + firstStepsList.length)"
      @mouseenter="() => handleFocusedStep(index + firstStepsList.length)"
      @mouseover="
        () =>
          /* ToDo: Fix when hovering the last step but proposal is the currentStep */ handleFocusedStep(
            index + firstStepsList.length,
          )
      "
      @touchstart="() => handleFocusedStep(index + firstStepsList.length)"
    >
      <StepTradeIn
        v-if="step.id === 'trade_in' && product.isTradeInEligible"
        :category="product.tracking.categoryName"
        :is-focused="isFocusedStep(index + firstStepsList.length)"
        :model="product.model"
        :price="priceWithoutSubsidies || undefined"
      />

      <StepKeyboard
        v-else-if="step.id === 'keyboard_type_language'"
        :product
        :step
      />

      <Step v-else :pickers :product :step>
        <template #title-grades>
          <RevLink
            v-if="step.guidance?.clickable"
            class="body-2-link relative bottom-2 block pl-12 md:hidden"
            variant="link"
            @click="onGuidanceGradesLinkClick"
          >
            {{ i18n(translations.conditionModalLinkSmallScreen) }}
          </RevLink>
        </template>

        <template #description-grades>
          <p class="body-2 mb-16 block md:hidden">
            {{ i18n(translations.conditionDescription) }}
          </p>
        </template>

        <template #description-mobile_plan>
          <StepDescriptionMobilePlan :offers />
        </template>

        <template #guidance-color>
          <Gallery
            :allow-media-viewer="false"
            :allow-partners-images="false"
            class="mb-16 w-full"
            :images
            :should-preload-first-img="false"
            :tracking-product-model="product.model"
            tracking-zone="pp_step_color_carousel"
          />
        </template>

        <template #step-end-mobile_plan>
          <StepEndMobilePlan :step :tracking-model="product.model" />
        </template>

        <template #step-end-dual_sim>
          <VisibleByVerizonSIMAdvertisement
            v-if="
              hasPartnerPromoCode(
                product.includedServiceOffers.partnerPromoCodes,
                'VISIBLE',
              )
            "
          />
        </template>

        <template #guidance-grades>
          <ConditionGuidance
            :brand="product.brand"
            :content="step.sideContent?.content"
            :tracking-model="product.model"
          />
        </template>
      </Step>
    </aside>

    <ErrorBoundary :owners="['bot-squad-b2cservices-front']">
      <PromoCodeStep
        :category="product.tracking.categoryName"
        :model="product.model"
        :partner-promo-codes="product.includedServiceOffers.partnerPromoCodes"
      />

      <MobilePlanStep
        v-if="product.model"
        :category-id="product.tracking.categoryId"
        :tracking-model="product.model"
      />
    </ErrorBoundary>

    <Proposal
      v-if="!hasNoVariants && (funnel.length > 0 || funnel.length === 1)"
      ref="proposal"
      :is-focused="isProposalFocused"
      :mobile-plan="selectedMobilePlan"
      :price
      :price-without-subsidies
      :product
      :selected-color-code
      :selected-offer
      :selected-options
      :tracking="productTracking"
      :upgrade-picker-fields
      @focus="() => handleFocusedProposal()"
      @mouseenter="() => handleFocusedProposal()"
    >
      <template #details>
        <slot name="details" />
      </template>
    </Proposal>
    <div v-if="hasNoVariants" class="ml-auto mr-0 md:w-2/3 md:pl-32 lg:w-1/2">
      <AdditionalInfo
        v-if="
          technicalSpecifications &&
          selectedOffer &&
          selectedOffer.offerLegacyId
        "
        :instructions="product.instructions"
        :listing-id="selectedOffer.offerLegacyId"
        :product-title="product.titles.default"
        :technical-specifications
      />

      <Vat v-if="isVatIncluded" class="my-32 md:my-56" />
    </div>
  </div>

  <div :id="STICKY_BARS_PORTAL_TARGET" />

  <ClientOnly>
    <NewBatteryModal
      :brand="product.brand"
      :category-id="product.tracking.categoryId"
      :device-name="product.titles.raw"
    />
    <SimModal
      v-if="showSimModal"
      :supports-apple-intelligence="product.supportsAppleIntelligence"
    />
    <ProcessorModal v-if="showProcessorModal" />
    <BouyguesOffersDrawer v-if="showBouyguesModal" :offers />
    <ConditionDescriptionModal
      v-if="funnel[0]"
      :brand="product.brand"
      :category-id="product.tracking.categoryId"
      :options="funnel[0].options"
    />
  </ClientOnly>
</template>

<script lang="ts" setup>
import { navigateTo, useRoute, useState } from '#imports'
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from 'vue'

import type { Price } from '@backmarket/http-api'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-b2c-services/mobile-plan/types/mobile-plan-offers'
import type { GetBreadcrumbResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/breadcrumb'
import {
  type GetPickersResponse,
  type GetServicesPickersResponse,
  hasItemExtraData,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { GetTechnicalSpecificationsResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/technical-specifications'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/composables/buybackOffer/useBuybackOffer'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import ErrorBoundary from '@backmarket/nuxt-module-logger/components/ErrorBoundary.vue'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { useIntersectionObserver } from '@vueuse/core'

import StepTradeIn from '~/scopes/buyback/swap/components/StepSwap/StepTradeIn.vue'
import AdditionalInfo from '~/scopes/product-core/components/presentationals/AdditionalInfo/AdditionalInfo.vue'
import Gallery from '~/scopes/product-core/components/presentationals/Gallery/Gallery.vue'
import Vat from '~/scopes/product-core/components/presentationals/Vat/Vat.vue'
import { useProductTracking } from '~/scopes/product-core/composables/useProductTracking/useProductTracking'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import { hasPartnerPromoCode } from '~/scopes/product-core/utils/hasPartnerPromoCode/hasPartnerPromoCode'

import { MODAL_NAMES, STICKY_BARS_PORTAL_TARGET } from '../../constants'

import translations from './CustomizationFunnel.translations'
import BouyguesOffersDrawer from './components/BouyguesOffersDrawer/BouyguesOffersDrawer.vue'
import ConditionDescriptionModal from './components/ConditionDescriptionModal/ConditionDescriptionModal.vue'
import ConditionGuidance from './components/ConditionGuidance/ConditionGuidance.vue'
import MobilePlanStep from './components/MobilePlanStep/MobilePlanStep.vue'
import NewBatteryModal from './components/NewBatteryModal/NewBatteryModal.vue'
import ProcessorModal from './components/ProcessorModal/ProcessorModal.vue'
import PromoCodeStep from './components/PromoCodeStep/PromoCodeStep.vue'
import Proposal from './components/Proposal/Proposal.vue'
import SimModal from './components/SimModal/SimModal.vue'
import Step from './components/Step/Step.vue'
import StepDescriptionMobilePlan from './components/StepDescriptionMobilePlan/StepDescriptionMobilePlan.vue'
import StepEndMobilePlan from './components/StepEndMobilePlan/StepEndMobilePlan.vue'
import StepIncompatibleKeyboard from './components/StepIncompatibleKeyboard/StepIncompatibleKeyboard.vue'
import StepKeyboard from './components/StepKeyboard/StepKeyboard.vue'
import VisibleByVerizonSIMAdvertisement from './components/VisibleByVerizonSIMAdvertisement/VisibleByVerizonSIMAdvertisement.vue'
import { createCustomizationFunnelData } from './utils/createCustomizationFunnelData'
import type { Step as StepType } from './utils/types'

const props = withDefaults(
  defineProps<{
    breadcrumb?: GetBreadcrumbResponse | null | undefined
    selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
    pickers: GetPickersResponse
    pickersServices: GetServicesPickersResponse['pickerGroups']
    isServicesPickersResponsePending: boolean
    price: Price
    priceWithoutSubsidies?: Price | null
    priceWithoutTradeIn?: Price | null
    product: GetProductResponse
    productTracking: ReturnType<typeof useProductTracking>
    selectedMobilePlan?: MobilePlanOffer
    technicalSpecifications?: GetTechnicalSpecificationsResponse | null
    upgradePickerFields?: NonNullable<
      GetPickersResponse['upgrade']
    >['pickerFields']
  }>(),
  {
    breadcrumb: undefined,
    selectedMobilePlan: undefined,
    priceWithoutSubsidies: undefined,
    priceWithoutTradeIn: null,
    technicalSpecifications: undefined,
    upgradePickerFields: undefined,
  },
)

const route = useRoute()
const i18n = useI18n()
const marketPlace = useMarketplace()
const { trackClick } = useTracking()
const { getDiscountedPrice } = useBuybackOffer()
const proposal = useTemplateRef<HTMLDivElement>('proposal')
const proposalObserver = ref<ReturnType<typeof useIntersectionObserver>>()
const lastYPosition = ref(0)
const scrollDirection = ref<'up' | 'down'>()
const currentStep = useState('current-step', () => 0)
// For the 2 following lines, have to use shallowRef instead of useTemplateRefsList
// Please see https://github.com/BackMarket/front-apps/pull/11560#discussion_r2358972073
const steps = shallowRef<HTMLDivElement[] | null>([])
const firstStepsRefs = shallowRef<HTMLDivElement[] | null>([])
const stepsObserver = ref<ReturnType<typeof useIntersectionObserver>>()
const stepsIntersections = ref<Record<string, number>>({})
const { mobilePlan } = useUrlParams()
const experiments = useExperiments()

const firstStepsList = computed(() => {
  const gradeSteps = []

  if (firstStepsRefs.value) {
    gradeSteps.push(...firstStepsRefs.value)
  }

  return gradeSteps
})

const stepsConcat = computed(() => {
  const stepsRef = steps.value ? [...steps.value] : []

  return [...firstStepsList.value, ...stepsRef]
})

/**
 * List of steps that conform
 * the page funnel.
 */
const funnel = computed(() => {
  return createCustomizationFunnelData(
    i18n,
    props.pickers,
    props.pickersServices,
    props.product,
    props.priceWithoutSubsidies,
    getDiscountedPrice(props.priceWithoutTradeIn),
    experiments,
  )
})

const hasKeyboardStep = computed(() => {
  return funnel.value?.find((step) => step.id === 'keyboard_type_language')
})

const hasNoVariants = computed(() => {
  return props.pickers.pickerGroups.length === 0
})

const selectedOptions = computed(() => {
  const variants = funnel.value
    ?.reduce<Array<string>>((acc, { options, type }) => {
      // We don't want to display in the sticky bar or the proposal the following steps:
      // - services
      if (type !== 'service') {
        const label =
          options?.find(({ selected }) => !!selected)?.label ?? undefined
        if (label) {
          acc.push(typeof label === 'object' ? i18n(label) : label)
        }
      }

      return acc
    }, [])
    .filter(Boolean)

  if (variants.length === 0) {
    return [props.product.titles.raw]
  }

  return variants
})

const isVatIncluded = computed(() => {
  return marketPlace.features.vatIncluded
})

const selectedColorCode = computed(() => {
  return (
    funnel.value
      ?.find(({ id }) => id === 'color')
      ?.options?.find(({ selected }) => !!selected)?.color ?? undefined
  )
})

const offers = computed(() => {
  const mobilePlanItems = props.pickersServices?.find(
    ({ id }) => id === 'mobile_plan',
  )?.items

  if (!mobilePlanItems) return []

  return mobilePlanItems
    .filter(hasItemExtraData)
    .map(({ mobilePlanOffer }) => mobilePlanOffer)
})

const showSimModal = computed(() => {
  return props.pickers.pickerGroups?.some(({ id }) => id === 'dual_sim')
})
const showBouyguesModal = computed(() => {
  return props.pickersServices?.some(({ id }) => id === 'mobile_plan')
})
const showProcessorModal = computed(() => {
  return props.pickers.pickerGroups?.some(
    ({ id }) =>
      id === 'processor_type_and_speed' ||
      id === 'processor_type_and_graphic_card',
  )
})

const images = computed(() => {
  return props.product.images.slice(0, 3)
})

function onGuidanceGradesLinkClick(guidance: StepType['guidance']) {
  openModal(MODAL_NAMES.CONDITION_COMPARISON)

  if (guidance?.tracking) {
    trackClick({
      name: guidance.tracking.name,
      zone: guidance.tracking.zone,
    })
  }
}

function isStepObserverInitialized() {
  return (
    stepsConcat.value &&
    stepsConcat.value.length > 0 &&
    stepsObserver.value?.isActive
  )
}

function handleFocusedStep(index: number) {
  if (isStepObserverInitialized()) {
    currentStep.value = index
  }
}

const isProposalObserverInitialized = computed(() => {
  return proposal.value && proposalObserver.value?.isActive
})

function handleFocusedProposal() {
  if (isProposalObserverInitialized.value && funnel.value) {
    currentStep.value = funnel.value.length
  }
}

const isProposalFocused = computed(() => {
  return isProposalObserverInitialized.value && funnel.value
    ? currentStep.value === funnel.value.length
    : true
})

function isFocusedStep(index: number) {
  return isStepObserverInitialized() ? currentStep.value === index : true
}

function addIntersectionObservers() {
  stepsObserver.value = useIntersectionObserver(
    stepsConcat,
    (entries) => {
      entries.forEach(({ target, intersectionRatio }) => {
        const index = stepsConcat.value.findIndex(
          (element) => element === target,
        )

        if (index == null) {
          return
        }

        stepsIntersections.value = {
          ...stepsIntersections.value,
          [index]: intersectionRatio,
        }
      })

      scrollDirection.value =
        lastYPosition.value > window.scrollY ? 'up' : 'down'
      lastYPosition.value = window.scrollY
    },
    {
      threshold: [0, 0.25, 0.5, 0.75, 1],
    },
  )

  proposalObserver.value = useIntersectionObserver(
    proposal.value,
    ([{ boundingClientRect, isIntersecting, intersectionRatio }]) => {
      // When more than 3/4 of a step is displaying, it became the focus state.
      // We remove the focus the previous step when it starts to disappear.
      if (
        isIntersecting &&
        parseFloat((Math.round(intersectionRatio * 4) / 4).toFixed(2)) >= 0.25
      ) {
        if (
          (lastYPosition.value > window.scrollY &&
            boundingClientRect.top < 0) ||
          (lastYPosition.value <= window.scrollY && boundingClientRect.top > 0)
        ) {
          handleFocusedProposal()
        }
      }

      scrollDirection.value =
        lastYPosition.value > window.scrollY ? 'up' : 'down'
      lastYPosition.value = window.scrollY
    },
    {
      threshold: [0, 0.25, 0.5, 0.75, 1],
    },
  )
}

function clearIntersectionObservers() {
  stepsIntersections.value = {}

  stepsObserver.value?.stop()
  stepsObserver.value = undefined

  proposalObserver.value?.stop()
  proposalObserver.value = undefined
}

onMounted(() => {
  addIntersectionObservers()
})

onUnmounted(() => {
  clearIntersectionObservers()
})

watch([funnel], () => {
  clearIntersectionObservers()
  addIntersectionObservers()
})

watch(stepsIntersections, () => {
  let intersections = Object.entries(stepsIntersections.value)

  if (!intersections.length) {
    return
  }

  if (scrollDirection.value === 'down') {
    intersections = intersections.reverse()
  }

  const step = intersections.reduce((acc, [index, intersectionRatio]) => {
    if (!acc) return [index, intersectionRatio]
    if (acc[1] < intersectionRatio) return [index, intersectionRatio]

    return acc
  })

  if (step) {
    const [index, intersectionRatio] = step
    const currentStepIndex = parseInt(index, 10)
    // only select currentStep, if the step is 100% visible
    // but skip this if the currentStep is the last step (Tada/Proposal)
    if (intersectionRatio === 1 && !isProposalFocused.value) {
      handleFocusedStep(currentStepIndex)
    }
  }
})

watch(
  [() => props.pickersServices],
  async () => {
    const areMobilePlanAvailable = funnel.value
      .find(({ id }) => id === 'mobile_plan')
      ?.options.slice(1)
      .every((o) => !o.available)

    if (areMobilePlanAvailable && mobilePlan.value) {
      await navigateTo({
        path: route.path,
        query: {
          ...route.query,
          mobilePlanOfferId: undefined,
        },
        hash: route.hash,
        replace: true,
      })
    }
  },
  { immediate: true },
)
</script>
